import React, { useEffect, useState } from "react";
import API from "../../../services/api";
import {
    captureGraphPreset,
    presetApiDetail,
    restoreGraphPreset,
    validatePresetName,
} from "../../../utils/graphPresets";
import ManageGraphPresetsDialog from "./ManageGraphPresetsDialog";
import SaveGraphPresetDialog from "./SaveGraphPresetDialog";

const GraphPresetBar = ({
    open,
    charts,
    appearance,
    layout,
    tags,
    tagsReady,
    tagsError,
    liveEnabled,
    dataMode,
    onApply,
}) => {
    const [presets, setPresets] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const [messageTone, setMessageTone] = useState("");
    const [saveOpen, setSaveOpen] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [manageOpen, setManageOpen] = useState(false);
    const [confirmUpdate, setConfirmUpdate] = useState(false);

    const refresh = async () => {
        const response = await API.get("/graph-presets/");
        const rows = Array.isArray(response.data) ? response.data : [];
        setPresets(rows);
        return rows;
    };

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                const response = await API.get("/graph-presets/");
                if (!active) {
                    return;
                }
                setPresets(Array.isArray(response.data) ? response.data : []);
                setMessage("");
            } catch (error) {
                if (!active) {
                    return;
                }
                setMessage(error?.response?.status === 403
                    ? "Sign in to save graph presets."
                    : "Unable to load graph presets.");
                setMessageTone("error");
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            active = false;
        };
    }, []);

    const report = (text, tone = "") => {
        setMessage(text);
        setMessageTone(tone);
    };

    const fail = (error, fallback) => {
        report(presetApiDetail(error, fallback), "error");
    };

    const savePreset = async (name) => {
        const nameError = validatePresetName(name, presets);
        if (nameError) {
            setSaveError(nameError);
            return;
        }
        const captured = captureGraphPreset(charts, appearance, layout);
        if (!captured.ok) {
            setSaveError(captured.error);
            return;
        }
        setBusy(true);
        setSaveError("");
        try {
            const response = await API.post("/graph-presets/", {
                name: name.trim(),
                configuration: captured.configuration,
            });
            const rows = await refresh();
            setPresets(rows);
            setSelectedId(String(response.data.id));
            setSaveOpen(false);
            report(`Saved ${response.data.name}.`);
        } catch (error) {
            setSaveError(presetApiDetail(error, "Unable to save this preset."));
        } finally {
            setBusy(false);
        }
    };

    const updatePreset = async () => {
        const preset = presets.find((item) => String(item.id) === String(selectedId));
        if (!preset) {
            report("Select a preset to update.", "error");
            setConfirmUpdate(false);
            return;
        }
        const captured = captureGraphPreset(charts, appearance, layout);
        if (!captured.ok) {
            report(captured.error, "error");
            setConfirmUpdate(false);
            return;
        }
        setBusy(true);
        try {
            const response = await API.patch(`/graph-presets/${preset.id}/`, {
                configuration: captured.configuration,
            });
            await refresh();
            setSelectedId(String(response.data.id));
            setConfirmUpdate(false);
            report(`Updated ${response.data.name}.`);
        } catch (error) {
            fail(error, "Unable to update this preset.");
            setConfirmUpdate(false);
        } finally {
            setBusy(false);
        }
    };

    const loadPreset = async () => {
        const preset = presets.find((item) => String(item.id) === String(selectedId));
        if (!preset) {
            report("Select a preset to load.", "error");
            return;
        }
        if (tagsError) {
            report("Unable to check which tags are available.", "error");
            return;
        }
        if (!tagsReady) {
            report("Tags are still loading.", "error");
            return;
        }
        setBusy(true);
        try {
            const response = await API.get(`/graph-presets/${preset.id}/`);
            let nextId = Date.now();
            const restored = restoreGraphPreset(
                response.data.configuration,
                tags,
                () => {
                    nextId += 1;
                    return nextId;
                },
            );
            if (restored.ok) {
                onApply(restored);
                const follow = dataMode === "live" && liveEnabled
                    ? " Live monitoring continues with these tags."
                    : " The time range is unchanged.";
                report(`Loaded ${response.data.name}.${restored.warning ? ` ${restored.warning}` : ""}${follow}`, restored.warning ? "warn" : "");
            } else {
                report(restored.error, "error");
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                await refresh().catch(() => {});
                setSelectedId("");
            }
            fail(error, "Unable to load this preset.");
        } finally {
            setBusy(false);
        }
    };

    const renamePreset = async (presetId, name) => {
        setBusy(true);
        try {
            await API.patch(`/graph-presets/${presetId}/`, { name });
            await refresh();
            report(`Renamed preset to ${name.trim()}.`);
        } catch (error) {
            fail(error, "Unable to rename this preset.");
        } finally {
            setBusy(false);
        }
    };

    const deletePreset = async (presetId) => {
        setBusy(true);
        try {
            await API.delete(`/graph-presets/${presetId}/`);
            const rows = await refresh();
            if (String(selectedId) === String(presetId)) {
                setSelectedId("");
            }
            const removed = presets.find((item) => item.id === presetId);
            report(`Deleted ${removed?.name || "preset"}.`);
            if (rows.length === 0) {
                setManageOpen(false);
            }
        } catch (error) {
            fail(error, "Unable to delete this preset.");
        } finally {
            setBusy(false);
        }
    };

    const selected = presets.find((item) => String(item.id) === String(selectedId));

    return (
        open ? <section id="graph-presets-panel" className="graph-page__card" aria-labelledby="graph-presets-heading">
            <div className="graph-page__card-head">
                <h2 id="graph-presets-heading">Favorite Graph Presets</h2>
                <p>Save tags, colors, axes, visibility, and chart display for this account.</p>
            </div>
            <div className="graph-page__preset-bar">
                <div className="graph-page__field">
                    <label htmlFor="graph-preset">Preset</label>
                    <select
                        id="graph-preset"
                        value={selectedId}
                        onChange={(event) => setSelectedId(event.target.value)}
                        disabled={loading || presets.length === 0}
                    >
                        <option value="">{presets.length === 0 ? "No saved presets" : "Select a preset"}</option>
                        {presets.map((preset) => (
                            <option key={preset.id} value={preset.id}>{preset.name}</option>
                        ))}
                    </select>
                </div>
                <button type="button" className="graph-page__button graph-page__button--primary" onClick={loadPreset} disabled={busy || !selectedId}>
                    Load Preset
                </button>
            </div>
            <div className="graph-page__actions">
                <button type="button" className="graph-page__button" aria-label="Save New Preset" onClick={() => { setSaveError(""); setSaveOpen(true); }} disabled={busy}>
                    + Save New
                </button>
                <button type="button" className="graph-page__button" aria-label="Update Preset" onClick={() => setConfirmUpdate(true)} disabled={busy || !selectedId}>
                    Update
                </button>
                <button type="button" className="graph-page__button" aria-label="Manage Presets" onClick={() => setManageOpen(true)} disabled={loading}>
                    Manage
                </button>
            </div>
            {loading && <p className="graph-page__empty">Loading presets…</p>}
            {!loading && presets.length === 0 && !message && (
                <p className="graph-page__empty">You have no saved presets yet.</p>
            )}
            {message && (
                <p className={`graph-page__banner${messageTone ? ` is-${messageTone}` : ""}`} role={messageTone === "error" ? "alert" : "status"}>
                    {message}
                </p>
            )}
            <SaveGraphPresetDialog
                open={saveOpen}
                saving={busy}
                error={saveError}
                onCancel={() => setSaveOpen(false)}
                onSave={savePreset}
            />
            <ManageGraphPresetsDialog
                open={manageOpen}
                presets={presets}
                tags={tags}
                busy={busy}
                onClose={() => setManageOpen(false)}
                onRename={renamePreset}
                onDelete={deletePreset}
            />
            {confirmUpdate && selected && (
                <div className="graph-page__dialog-backdrop" onMouseDown={() => setConfirmUpdate(false)}>
                    <div
                        className="graph-page__dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="update-preset-title"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <h3 id="update-preset-title">Update Preset</h3>
                        <p>Replace the saved tags, colors, axes, visibility, and chart display in {selected.name} with the current graph?</p>
                        <div className="graph-page__actions">
                            <button type="button" className="graph-page__button graph-page__button--primary" disabled={busy} onClick={updatePreset}>
                                {busy ? "Updating…" : "Update Preset"}
                            </button>
                            <button type="button" className="graph-page__button" onClick={() => setConfirmUpdate(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </section> : null
    );
};

export default GraphPresetBar;
